import React, { useState, useCallback, useEffect, useMemo } from 'react'
import KanbanView from '@/components/KanbanView'
import TableView from '@/components/TableView'
import { Columns3, Dot, TableProperties, X, Loader2, Download } from "lucide-react"
import api from '@/http/api'
import { Button } from "@univdiam/ui/components/button"
import { Badge } from "@univdiam/ui/components/badge"
import { Input } from "@univdiam/ui/components/input"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@univdiam/ui/components/dropdown-menu"
import { useOrder, useInfiniteOrder, useUpdateOrder, useSubmittedCount } from '@/sharedData/Order'
import { useOrderTags } from '@/sharedData/OrderTags'
import { useTableDataColumn } from '@univdiam/ui/lib/TableDataColumn'
import { getColumns } from '@univdiam/ui/customComponents/OrderList/columns'
import type { OrderType } from '@/types'
import Loader from "@/components/Loader";
import EmptyScreen from '@univdiam/ui/customComponents/EmptyScreen'
import { kanbanColumns } from "@univdiam/ui/lib/KanbanColumns";
import { ScrollArea } from '@univdiam/ui/components/scroll-area'
import { exportOrdersToCsv } from '@/lib/exportOrders'
import { toast } from 'sonner'


import { useNavigate } from 'react-router-dom'
import { useDashboardView, useOrderNav } from '@/store'

// Per column, not per board. Ten fills a column on first paint; the rest
// arrives as the user scrolls that specific column.
const KANBAN_PAGE_SIZE = 10

const Dashbaord = () => {
  const navigate = useNavigate()

  // All of this is persisted to localStorage by the store, so filters survive
  // opening an order and coming back regardless of how the user navigates.
  const {
    view, setView,
    searchQuery, setSearchQuery,
    page, setPage,
    tagFilter, setTagFilter,
    submittedOnly, setSubmittedOnly,
    sorting, setSorting,
    columnVisibility, setColumnVisibility,
    kanbanColumnVisibility: storedKanbanVisibility, setKanbanColumnVisibility,
  } = useDashboardView()

  const setOrderNav = useOrderNav((s) => s.setOrderNav)

  const [searchInput, setSearchInput] = useState(searchQuery)
  const [isExporting, setIsExporting] = useState(false)
  // Tags are a server-side filter now, so totalPages counts matching orders --
  // page 3 of the old result set is meaningless against the new one.
  const toggleTagFilter = (name: string) => {
    setTagFilter(tagFilter.includes(name) ? tagFilter.filter((t) => t !== name) : [...tagFilter, name])
    setPage(1)
  }

  // merged over the defaults so columns added to kanbanColumns later still show
  // up for users who already have a persisted visibility map
  const kanbanColumnVisibility = useMemo(() => {
    const defaults: Record<string, boolean> = {}
    for (const col of kanbanColumns) {
      defaults[col.id] = col.id !== 'cad_archive' && col.id !== 'completed_archive'
    }
    return { ...defaults, ...storedKanbanVisibility }
  }, [storedKanbanVisibility])

  const { data: submittedCount } = useSubmittedCount()
  const { data: tableData, isPending: isTablePending, isFetching: isTableFetching } = useOrder(searchQuery, page, 50, { enabled: view === 'Table' }, tagFilter, submittedOnly)

  // One infinite query per Kanban column, so a column pages on its own cursor
  // rather than every column sharing one window over the whole order list --
  // which is what used to leave sparse statuses empty until you paged past
  // everything newer. kanbanColumns is a module-level constant, so this map
  // always calls the same hooks in the same order: never filter it here, gate
  // the fetch with `enabled` instead.
  const columnQueries = kanbanColumns.map((col) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useInfiniteOrder(searchQuery, KANBAN_PAGE_SIZE, col.id, {
      enabled: view === 'Kanban' && kanbanColumnVisibility[col.id],
    }, tagFilter, submittedOnly)
  )

  const kanbanOrders: OrderType[] = columnQueries.flatMap(
    (q) => q.data?.pages.flatMap((p: any) => p.data as OrderType[]) ?? []
  )
  const hasKanbanData = columnQueries.some((q) => !!q.data)
  // isLoading, not isPending: a disabled query stays pending forever.
  const isKanbanLoading = columnQueries.some((q) => q.isLoading)
  const isKanbanFetching = columnQueries.some((q) => q.isFetching)

  // fetchNextPage is referentially stable and the rest are primitives, so a
  // fresh wrapper object each render does not retrigger the column's observer.
  const columnPagination = Object.fromEntries(
    kanbanColumns.map((col, i) => [col.id, {
      fetchNextPage: columnQueries[i]!.fetchNextPage,
      hasNextPage: !!columnQueries[i]!.hasNextPage,
      isFetchingNextPage: columnQueries[i]!.isFetchingNextPage,
    }])
  )

  const isPending = view === 'Table' ? isTablePending : isKanbanLoading;
  const isFetching = view === 'Table' ? isTableFetching : isKanbanFetching;
  const { data: orderTagsData } = useOrderTags()
  const adminTags = orderTagsData ?? []

  const { mutate: updateOrder } = useUpdateOrder()

  const handleUpdateOrder = (orderId: string, customerId: string, updates: any) => {
    updateOrder({ order_id: orderId, customer_id: customerId, updates })
  }

  const fetchTags = useCallback(async (page: number, limit: number, search: string) => {
    const response = await api.get('/admin/order-tags', {
      params: { page, limit, search }
    })
    return {
      tags: response.data.data,
      totalPages: response.data.pagination.totalPages
    }
  }, [])

  const columns = getColumns(true, handleUpdateOrder, adminTags, fetchTags)
  const table = useTableDataColumn(tableData?.data?.data || [], columns, setSorting, setColumnVisibility, "", sorting, columnVisibility)

  const newUpdateCount = view === 'Table'
    ? tableData?.data?.data?.filter((item: OrderType) => item.is_new_update).length || 0
    : kanbanOrders.filter((item: OrderType) => item.is_new_update).length;

  useEffect(() => {
    // guard keeps the mount pass from resetting the page of a restored search
    if (searchInput === searchQuery) return
    const timer = setTimeout(() => setSearchQuery(searchInput), 300)
    return () => clearTimeout(timer)
  }, [searchInput, searchQuery, setSearchQuery])

  const handleSearch = () => {
    setSearchQuery(searchInput)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      setSearchQuery(searchInput)
    }
  }

  const handleClearSearch = () => {
    setSearchInput("")
    setSearchQuery("")
  }

  // Exports every order, not just the page on screen, so it refetches the full
  // list itself rather than reading the dashboard's cached page.
  const handleExport = async () => {
    setIsExporting(true)
    try {
      const count = await exportOrdersToCsv()
      toast.success(`Exported ${count} orders`)
    } catch {
      toast.error('Export failed. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }

  const handleViewChange = (newView: 'Kanban' | 'Table') => {
    setView(newView)
  }


  const handleOrderOpen = (id: string, customer_id: string, order_ref: string) => {
    // Snapshot the siblings the user can arrow through from the detail page.
    // Kanban scopes to the clicked order's board; Table uses the sorted rows.
    let siblings: OrderType[] = []
    let label = 'Orders'

    if (view === 'Kanban') {
      const visible = kanbanOrders
      const status = visible.find((o) => String(o.id) === String(id))?.status
      siblings = status ? visible.filter((o) => o.status === status) : visible
      label = kanbanColumns.find((c) => c.id === status)?.title ?? label
    } else {
      siblings = table.getSortedRowModel().rows.map((r) => r.original as unknown as OrderType)
    }

    setOrderNav(
      siblings.map((o) => ({
        id: String(o.id),
        orderRef: o.order_id,
        customer_id: String(o.customer_id),
      })),
      label
    )
    navigate(`/order/${id}/${order_ref}/${customer_id}`)
  }


  const hasData = view === 'Table' ? !!tableData : hasKanbanData
  if (isPending && !hasData) {
    return <Loader />
  }

  // Columns settle one at a time now, so "no orders" only holds once nothing is
  // still in flight -- otherwise the empty screen flashes mid-load.
  const isEmpty = view === 'Table'
    ? tableData?.data?.data?.length === 0
    : hasKanbanData && !isKanbanFetching && kanbanOrders.length === 0;

  // Only the unfiltered dashboard may swap out the toolbar -- an empty search
  // or tag result must keep it so the filter can be cleared.
  if (isEmpty && !searchQuery && tagFilter.length === 0 && !submittedOnly) {
    return <EmptyScreen>
      <p className="flex items-center justify-center gap-1">Looks like you haven’t created any orders yet.</p>
    </EmptyScreen>
  }

  return (
    <div id="dashboard" className="p-[24px] min-h-screen w-full">
      <div className='bg-white rounded-lg border border-[#E5E5E5] overflow-hidden'>
        {/* header */}
        <div id='header' className="bg-white">
          <div className="p-4 md:px-5 md:py-3 border-b flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 lg:gap-0">
            {/* Title & Badge */}
            <div className="flex gap-2 justify-between lg:justify-start items-center w-full lg:w-auto">
              <p className="font-medium text-lg">My Orders</p>
              <Badge
                variant="outline"
                className="px-3 py-1 text-xs font-medium text-black border border-[#CFCFD1] rounded-sm"
              >
                <Dot size={6} strokeWidth={10} color='#00B878' className='rounded-full' />
                {newUpdateCount} new updates
              </Badge>
            </div>

            {/* Controls */}
            <div className="flex flex-col sm:flex-row gap-3 items-center w-full lg:w-auto">

              {/* View Toggle */}
              <div className="flex w-full sm:w-auto gap-1 p-1 bg-[#f6f6f6] rounded-md items-center justify-center sm:justify-start order-2 sm:order-1">
                {/* Kanban View Button */}
                <Columns3
                  className={`w-[28px] h-[28px] p-1.5 relative rounded-[2px] cursor-pointer transition-all duration-200
                    ${view === 'Kanban' ? 'bg-white shadow-sm border border-border ' : 'bg-transparent text-muted-foreground hover:text-foreground'}`}
                  size={16}
                  strokeWidth={1.5}
                  onClick={() => handleViewChange('Kanban')}
                />

                {/* Table View Button */}
                <TableProperties
                  className={`w-[28px] h-[28px] p-1.5 relative rounded-[2px] cursor-pointer transition-all duration-200
                    ${view === 'Table' ? 'bg-white shadow-sm border border-border ' : 'bg-transparent text-muted-foreground hover:text-foreground'}`}
                  size={16}
                  strokeWidth={1.5}
                  onClick={() => handleViewChange('Table')}
                />
              </div>

              {/* Search Bar */}
              <div className="relative w-full sm:w-auto order-1 sm:order-2 flex gap-2">
                <div className="relative w-full sm:w-64 lg:w-lg">
                  <Input
                    placeholder="Search by order ID, title or customer..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="w-full"
                  />
                  {
                    searchInput !== '' && <X className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 cursor-pointer" onClick={handleClearSearch} />
                  }
                </div>
                <Button type="button" variant="default" onClick={handleSearch} className="px-6 min-w-24 h-[34px]" disabled={isFetching}>
                  {isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
                </Button>
              </div>

              {/* Tags */}
              {adminTags.length > 0 && (
                <div className="w-full sm:w-auto order-3 sm:order-3 flex justify-end sm:justify-start">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="w-full sm:w-auto gap-1.5">
                        Tags
                        {tagFilter.length > 0 && (
                          <Badge variant="outline" className="px-1.5 py-0 text-xs rounded-full">
                            {tagFilter.length}
                          </Badge>
                        )}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="max-h-72 overflow-y-auto">
                      {tagFilter.length > 0 && (
                        <>
                          <DropdownMenuItem
                            className="text-muted-foreground"
                            onSelect={(e) => { e.preventDefault(); setTagFilter([]) }}
                          >
                            Clear filters
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                        </>
                      )}
                      {adminTags.map((tag) => (
                        <DropdownMenuCheckboxItem
                          key={tag.id}
                          checked={tagFilter.includes(tag.name)}
                          onSelect={(e) => e.preventDefault()}
                          onCheckedChange={() => toggleTagFilter(tag.name)}
                        >
                          <span
                            className="inline-block w-2 h-2 rounded-full mr-1.5 shrink-0"
                            style={{ backgroundColor: tag.color }}
                          />
                          {tag.name}
                        </DropdownMenuCheckboxItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}

              {/* Columns */}
              <div className="w-full sm:w-auto order-4 sm:order-4 flex justify-end sm:justify-start">
                {view === 'Table' ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="w-full sm:w-auto">
                        Columns
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {table
                        .getAllColumns()
                        .filter(
                          (column) => column.getCanHide()
                        )
                        .map((column) => {
                          return (
                            <DropdownMenuCheckboxItem
                              key={column.id}
                              className="capitalize"
                              checked={column.getIsVisible()}
                              onCheckedChange={(value) =>
                                column.toggleVisibility(!!value)
                              }
                            >
                              {column.id}
                            </DropdownMenuCheckboxItem>
                          )
                        })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="w-full sm:w-auto">
                        Columns
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {kanbanColumns.map((col) => (
                        <DropdownMenuCheckboxItem
                          key={col.id}
                          className="capitalize"
                          checked={kanbanColumnVisibility[col.id]}
                          onCheckedChange={(value) =>
                            setKanbanColumnVisibility((prev) => ({
                              ...prev,
                              [col.id]: !!value,
                            }))
                          }
                        >
                          {col.title}
                        </DropdownMenuCheckboxItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>

              {/* Export */}
              <div className="w-full sm:w-auto order-5 sm:order-5 flex justify-end sm:justify-start">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleExport}
                  disabled={isExporting}
                  className="w-full sm:w-auto gap-1.5"
                >
                  {isExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  {isExporting ? 'Exporting...' : 'Export'}
                </Button>
              </div>
            </div>
          </div>
        </div>
        {/* Views */}
        <div id='views'>
          {isEmpty && (tagFilter.length > 0 || submittedOnly) ? (
            <div className="h-[calc(100vh-8rem)] flex flex-col items-center justify-center gap-3 text-muted-foreground">
              <p>No orders match these filters.</p>
              <Button variant="outline" onClick={() => { setTagFilter([]); setSubmittedOnly(false); setPage(1) }}>Clear filters</Button>
            </div>
          ) : (
          <ScrollArea className="">
            {
              view == 'Kanban' ? <div className='p-[20px] overflow-x-scroll h-[calc(100vh-8rem)]'>
                {/* Click to show only flagged orders; the count spans every
                    column and page, so this is how to find them all. */}
                {(!!submittedCount || submittedOnly) && (
                  <button
                    type='button'
                    onClick={() => setSubmittedOnly(!submittedOnly)}
                    aria-pressed={submittedOnly}
                    className='mb-3 flex items-center gap-1.5 text-sm font-medium text-[#1F7A4D] hover:underline'
                  >
                    <span className='size-2 rounded-full bg-[#22A06B]' aria-hidden='true' />
                    {submittedOnly
                      ? 'Showing submitted orders only · Show all'
                      : `${submittedCount} ${submittedCount === 1 ? 'order' : 'orders'} submitted, waiting for a stage move · Show`}
                  </button>
                )}
                <KanbanView
                data={kanbanOrders}
                handleOrderOpen={handleOrderOpen}
                columnVisibility={kanbanColumnVisibility}
                isAdmin={true}
                adminTags={adminTags}
                columnPagination={columnPagination}
              /></div> :
                <div className='overflow-y-auto h-[calc(100vh-8rem)]'><TableView
                  data={tableData?.data?.data || []}
                  pagination={tableData?.data?.pagination}
                  page={page}
                  setPage={setPage}
                  isAdmin={true}
                  onUpdateOrder={handleUpdateOrder}
                  adminTags={adminTags}
                  fetchTags={fetchTags}
                  {...{ globalFilter: tagFilter, setGlobalFilter: setTagFilter, sorting, setSorting, columnVisibility, setColumnVisibility, handleOrderOpen }}
                /></div>
            }
          </ScrollArea>
          )}
        </div>
      </div>
    </div>

  )
}

export default Dashbaord